/* Drie lijsten op de site die in de opzet van een soort content passen (./website, ADR 0024): het
 * team op de pagina Over ons, en de citaten en de logo's op de homepage. Een rij heeft een `id` en een
 * volgnummer, geen slug en geen eigen pagina.
 *
 * Wat de site met een kolom doet, staat in de site-repo: components/groep-c/TeamGrid.tsx (team),
 * components/shared/TestimonialsSlider.tsx (citaten) en components/shared/PartnersSlider.tsx (logo's).
 * De uitleg bij de velden hieronder komt daaruit.
 *
 * De regels:
 *   - Alle drie hebben een publicatievlag: de site toont alleen een gepubliceerde rij. In de database
 *     staat hij standaard aan; de CRM maakt iets nieuws altijd aan als concept (maakNieuw in ./website),
 *     zodat er niets per ongeluk op de site komt.
 *   - Het team heeft die vlag sinds het besluit van 10-10-2026. De kolom komt uit een script dat de
 *     eigenaar van de database draait (SCRIPT_TEAM); bestaande teamleden zijn daarna gepubliceerd. Heeft
 *     een rij de kolom nog niet, dan staat ze gewoon op de site en wordt de vlag nooit geschreven
 *     (kentPublicatie in ./website).
 *   - Er wordt hier niets verwijderd. Een teamlid, een citaat of een logo haal je van de site door het
 *     terug te zetten naar concept.
 *   - Een adres, een e-mailadres en een telefoonnummer worden nagekeken als iemand ze wijzigt. De site
 *     zet ze ongezien in een link. */

import { isAdres } from './instellingen';
import type { ContentSoort, Veld } from './website';

const keurAdres = (w: string): string | null => (/^\S+$/.test(w) && isAdres(w) ? null : 'Vul het hele adres in. Het begint met https://.');
const keurEmail = (w: string): string | null => (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(w) ? null : 'Dit is geen e-mailadres.');
const keurTelefoon = (w: string): string | null => (/\d/.test(w) ? null : 'Een telefoonnummer bevat cijfers.');
/* De site tekent zoveel sterren als hier staat (Array.from({ length: rating || 5 })): een getal buiten
 * 1 tot en met 5 geeft geen of te veel sterren. */
const keurSterren = (w: string): string | null => (/^[1-5]$/.test(w) ? null : 'Kies een heel getal van 1 tot en met 5.');

const VOLGORDE: Veld = { kolom: 'sort_order', label: 'Volgorde', soort: 'getal', uitleg: 'Het laagste getal staat vooraan.' };

/** Het script in de site-repo (greenvision-site) dat het team een publicatievlag geeft. */
export const SCRIPT_TEAM = 'db/voorstel/2026-10-11-team-kennisbank-flex-e.sql';

export const TEAM: ContentSoort = {
  sleutel: 'team', tabel: 'team_members', enkelvoud: 'Teamlid', meervoud: 'Team',
  padPrefix: null, titelKolom: 'name', heeftSlug: false, heeftPublicatie: true, publicatieScript: SCRIPT_TEAM,
  volgorde: 'sort_order', oplopend: true,
  uitleg: 'De kaarten onder "Ons team" op de pagina Over ons (/over-ons): een grote foto, de naam, de functie en knoppen voor LinkedIn, mail en telefoon. Alleen een gepubliceerd teamlid staat er.',
  letOp: 'Een gepubliceerd teamlid staat met naam, functie en foto op de pagina Over ons. Iets nieuws begint daarom als concept: het staat pas op de site nadat je het zelf publiceert. Iemand van de site halen doe je door het teamlid terug te zetten naar concept: er wordt niets verwijderd.',
  velden: [
    { kolom: 'name', label: 'Naam', soort: 'tekst', verplicht: true },
    { kolom: 'role', label: 'Functie', soort: 'tekst' },
    { kolom: 'photo', label: 'Foto', soort: 'afbeelding', uitleg: 'Een staande foto: de site vult er de hele kaart mee. Zonder foto blijft dat vlak leeg.' },
    { kolom: 'linkedin', label: 'LinkedIn', soort: 'tekst', keur: keurAdres, uitleg: 'Het adres van het profiel. Leeg betekent: geen knop.' },
    { kolom: 'email', label: 'E-mailadres', soort: 'tekst', keur: keurEmail, uitleg: 'Staat als knop op de site: iedereen kan het adres zien. Leeg betekent: geen knop.' },
    { kolom: 'phone', label: 'Telefoonnummer', soort: 'tekst', keur: keurTelefoon, uitleg: 'Staat als knop op de site: iedereen kan het nummer zien. Leeg betekent: geen knop.' },
    VOLGORDE,
  ],
  ondertitel: ['role'], beeld: 'photo', volgnummer: true,
  blokken: [], alleenLezen: [],
};

export const CITATEN: ContentSoort = {
  sleutel: 'citaten', tabel: 'testimonials', enkelvoud: 'Citaat', meervoud: 'Citaten',
  padPrefix: null, titelKolom: 'name', heeftSlug: false, heeftPublicatie: true,
  volgorde: 'sort_order', oplopend: true,
  uitleg: 'Het blok "Wat onze klanten zeggen" op de homepage: de sterren, het citaat, een kleine ronde foto, de naam en daaronder de functie en het bedrijf. Alleen een gepubliceerd citaat staat er. Is er geen enkel gepubliceerd citaat, dan is er geen blok.',
  letOp: 'Een gepubliceerd citaat staat met naam, functie en bedrijf op de homepage. Iets nieuws begint daarom als concept: het staat pas op de site nadat je het zelf publiceert.',
  velden: [
    { kolom: 'name', label: 'Naam', soort: 'tekst', verplicht: true },
    { kolom: 'role', label: 'Functie', soort: 'tekst' },
    { kolom: 'company', label: 'Bedrijf', soort: 'tekst' },
    { kolom: 'quote', label: 'Citaat', soort: 'lang', verplicht: true, uitleg: 'Zonder aanhalingstekens: die zet de site er zelf omheen. Gewone tekst, geen HTML.' },
    { kolom: 'photo', label: 'Foto', soort: 'afbeelding', uitleg: 'De site toont hem klein en rond. Zonder foto staan er alleen de naam, de functie en het bedrijf.' },
    { kolom: 'rating', label: 'Sterren', soort: 'getal', verplicht: true, keur: keurSterren, uitleg: 'Het aantal sterren boven het citaat, van 1 tot en met 5.' },
    VOLGORDE,
  ],
  ondertitel: ['role', 'company'], beeld: 'photo', volgnummer: true, begin: { rating: 5 },
  blokken: [], alleenLezen: [],
};

export const LOGOS: ContentSoort = {
  sleutel: 'logos', tabel: 'partners', enkelvoud: 'Logo', meervoud: "Logo's",
  padPrefix: null, titelKolom: 'name', heeftSlug: false, heeftPublicatie: true,
  volgorde: 'sort_order', oplopend: true,
  uitleg: 'Het blok "Onze partners" op de homepage: een rij logo\'s, elk met een link naar de website van de partner. Alleen een gepubliceerd logo staat er. Is er geen enkel gepubliceerd logo, dan is er geen blok.',
  letOp: 'Een gepubliceerd logo staat op de homepage, met de naam van de partner. Iets nieuws begint daarom als concept: het staat pas op de site nadat je het zelf publiceert.',
  velden: [
    { kolom: 'name', label: 'Naam', soort: 'tekst', verplicht: true, uitleg: 'De tekst bij het logo voor wie het beeld niet ziet. Zonder logo staat de naam er als tekst.' },
    { kolom: 'logo', label: 'Logo', soort: 'afbeelding', uitleg: 'De site toont het logo in grijs, en in kleur als de muis erop staat.' },
    { kolom: 'website', label: 'Website', soort: 'tekst', keur: keurAdres, uitleg: 'Een klik op het logo opent dit adres in een nieuw tabblad. Zonder adres staat het logo er zonder link.' },
    { kolom: 'description', label: 'Omschrijving', soort: 'lang', uitleg: 'Voor eigen gebruik: de site toont de omschrijving nu niet.' },
    VOLGORDE,
  ],
  ondertitel: ['website'], beeld: 'logo', volgnummer: true,
  blokken: [], alleenLezen: [],
};

export const LIJSTEN: ContentSoort[] = [TEAM, CITATEN, LOGOS];
